import axios from 'axios';
import {axiosInstance} from '../services/axiosInstance';
import {
  handleUploadDocumentsService,
  handleUploadImagesService,
  handleUploadVideosService,
} from '../services/uploaders';

type Uploaded = {url: string; type: 'image' | 'video' | 'audio' | 'document'};

const getCategory = (mime: string): Uploaded['type'] => {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'document';
};

async function uploadFileThroughBackend(file: File): Promise<Uploaded> {
  const category = getCategory(file.type);
  const payload = new FormData();
  let response: {data?: string[]};

  if (category === 'image') {
    payload.append('images', file);
    response = await handleUploadImagesService(payload);
  } else if (category === 'video') {
    payload.append('videos', file);
    response = await handleUploadVideosService(payload);
  } else {
    payload.append('files', file);
    response = await handleUploadDocumentsService(payload);
  }

  const url = response?.data?.[0];
  if (!url) {
    throw new Error('Upload response did not include a file URL');
  }

  return {url, type: category};
}

export async function uploadFiles(files: File[]): Promise<Uploaded[]> {
  const uploaded: Uploaded[] = [];

  for (const file of files) {
    const mimeType = file.type || 'application/octet-stream';
    let hasPresignedUploadUrl = false;

    try {
      const presignResp = await axiosInstance.post('presign/s3', {
        fileName: file.name,
        mimeType,
      });

      const {uploadUrl, publicUrl} = presignResp.data as {
        uploadUrl: string;
        publicUrl: string;
        key: string;
      };
      hasPresignedUploadUrl = true;

      await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': mimeType,
        },
      });

      uploaded.push({
        url: publicUrl,
        type: getCategory(mimeType),
      });
    } catch (error) {
      const isForbidden =
        axios.isAxiosError(error) && error.response?.status === 403;

      if (!hasPresignedUploadUrl && !isForbidden) {
        throw error;
      }

      uploaded.push(await uploadFileThroughBackend(file));
    }
  }

  return uploaded;
}
